// The value picker (prototype picker dialog): Any value / Only these, a
// search, "Select all shown", Clear, whole GROUPS (when the design layer names
// a grouping column for this master property — plan §7.5; no declaration, no
// groups), and the values with their record counts. A list longer than
// limits.PICKER_LOCAL_MAX is searched by the server.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Info } from 'lucide-react'
import { tw } from '../../testHooks'
import { KitModal } from '../../designerKit/KitModal'
import type { UdaRuleValue } from '../types'
import { ANY, canonValue, normalize } from '../rules'
import { driverLabel, formOf } from './model'
import {
  closeValue, filterValues, groupPicked, isPicked, pickedText, selectShown, serverSearch, toggleGroup,
  toggleValue, type PickValue,
} from './picker'
import { opSet, useSetup } from './state'
import { readMasterValues } from './useDomains'
import { HintBox, Seg } from './ui'

export default function ValuePicker({ setKey, ruleIndex, driverKey, onClose }: {
  setKey: string; ruleIndex: number; driverKey: string; onClose: () => void
}) {
  const { formNo, draft, domains, transport, objName, notify } = useSetup()
  const form = formOf(draft.layer, formNo)
  const set = form?.sets.find(s => s.key === setKey)
  const d = form?.drivers.find(x => x.key === driverKey)
  const rule = set?.rules?.[ruleIndex]
  const value: UdaRuleValue | undefined = rule ? rule[driverKey] : undefined
  const dom = d ? domains.domainOf(formNo, d) : null
  const all = dom?.values || []
  const allCodes = useMemo(() => all.map(v => v[0]), [all])
  const [q, setQ] = useState('')
  const remote = serverSearch(dom?.total)
  const [found, setFound] = useState<PickValue[] | null>(null)
  const [searching, setSearching] = useState(false)
  const seq = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    if (!remote || !d || d.from.kind !== 'master') { setFound(null); return undefined }
    const needle = q.trim()
    if (!needle) { setFound(null); return undefined }
    const my = ++seq.current
    setSearching(true)
    const via = d.from.via
    const column = d.from.column
    const t = setTimeout(() => {
      void readMasterValues(transport, objName, formNo, via, column, needle).then(r => {
        if (my !== seq.current) return
        setSearching(false)
        if (r.error) notify('error', r.error)
        setFound(r.values)
      })
    }, 300)
    return () => clearTimeout(t)
  }, [q, remote, d, transport, objName, formNo, notify])

  const shown = found ?? filterValues(all, q)
  const any = canonValue(value) === ANY

  const write = (next: UdaRuleValue) => draft.edit(opSet(formNo, setKey, s => {
    const rules = [...(s.rules || [])]
    if (!rules[ruleIndex]) return
    rules[ruleIndex] = { ...rules[ruleIndex], [driverKey]: next }
    s.rules = rules
  }))

  const done = () => {
    const { value: v, becameAny } = closeValue(value)
    if (becameAny) notify('info', 'Nothing was picked, so it is set to Any.')
    draft.edit(opSet(formNo, setKey, (s, f) => {
      const rules = [...(s.rules || [])]
      if (rules[ruleIndex]) rules[ruleIndex] = { ...rules[ruleIndex], [driverKey]: v }
      s.rules = normalize(rules, f.drivers)
    }))
    onClose()
  }

  if (!set || !d || !rule) {
    return (
      <KitModal open onClose={onClose} title="Pick values">
        <div className="uds-na">This condition is no longer there.</div>
      </KitModal>
    )
  }
  return (
    <KitModal open onClose={done} title={driverLabel(d)}
      footer={<button type="button" className="btn btn-primary btn-sm" onClick={done} {...tw('action', 'uda-pick-done')}>Done</button>}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 10 }} {...tw('screen', 'uda-picker')}>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>for {set.label || set.key}</div>
        <Seg value={any ? 'any' : 'only'} ariaLabel="Which values"
          onChange={v => write(v === 'any' ? ANY : (any ? [] : canonValue(value) as UdaRuleValue))}
          options={[{ value: 'any', label: 'Any value', hook: 'uda-pick-any' }, { value: 'only', label: 'Only these', hook: 'uda-pick-only' }]} />
        {any && (
          <HintBox icon={<Info size={15} />} style={{ margin: 0 }}>
            Applies to every {driverLabel(d).toLowerCase()}, including ones added to the master later.
          </HintBox>
        )}
        <input ref={inputRef} className="uds-inp" placeholder={`Search ${dom?.total ?? 0} values`} value={q}
          aria-label="Search values" onChange={e => setQ(e.target.value)} {...tw('field', 'uda-pick-search')} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => write(selectShown(any ? [] : value, shown.map(v => v[0])))}>Select all shown</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => write([])}>Clear</button>
          <span className="uds-grow" />
          <span className="uds-hint" style={{ margin: 0 }}>{searching ? 'Searching…' : pickedText(value, dom?.total ?? all.length)}</span>
        </div>
        {!!dom?.groups.length && (
          <div>
            <div className="uds-lbl" style={{ margin: '0 0 6px' }}>Pick a whole group</div>
            <div className="uds-chips">
              {dom.groups.map(g => (
                <button key={g.label} type="button" className={`uds-vchip ${groupPicked(value, g.values) ? 'on' : ''}`}
                  onClick={() => write(toggleGroup(value, g.values))}>
                  {g.label || '(none)'}<span className="cnt">{g.values.length}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {dom?.loading && <div className="uds-hint"><span className="uds-spin" /> Reading the values…</div>}
        {dom?.error && <div className="uds-hint" style={{ color: 'var(--color-error)' }}>{dom.error}</div>}
        <div className="uds-pklist">
          {shown.map(([code, label, n]) => (
            <label key={code} className="uds-pkrow">
              <input type="checkbox" checked={isPicked(value, code)}
                onChange={() => write(toggleValue(value, code, allCodes))} {...tw('action', `uda-pick-value:${code}`)} />
              <span className="uds-grow">{label}</span>
              {label !== code && <span className="uds-mono" style={{ color: 'var(--color-text-faint)' }}>{code}</span>}
              {n != null && <span style={{ color: 'var(--color-text-faint)', minWidth: 48, textAlign: 'right' }}>{n.toLocaleString()}</span>}
            </label>
          ))}
          {!shown.length && !dom?.loading && <div className="uds-na" style={{ padding: 10 }}>No value matches.</div>}
        </div>
        {remote && !q.trim() && (
          <div className="uds-hint">Showing the first {all.length} of {dom?.total.toLocaleString()} values — type to search them all.</div>
        )}
      </div>
    </KitModal>
  )
}
